import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BookOpen, Clock, Loader2, MapPin, Plus, Search, Sparkles, Star, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { API, api } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";

const fmtMin = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ""}` : `${min}m`);
const photoUrl = (cityId, a) => (a.has_photo ? `${API}/public/cities/${cityId}/attractions/${a.id}/photo` : null);

const Section = ({ title, children, testId }) => (
    <section className="mt-10" data-testid={testId}>
        <h3 className="font-display text-xl text-amber-100">{title}</h3>
        <div className="mt-1 h-px w-14 bg-amber-500/50" />
        <div className="mt-4">{children}</div>
    </section>
);

/** Esplorazione immersiva di una città: storia, periodi, patrimonio, consigli,
 *  galleria attrazioni con filtri reali e schede "Scopri la storia". */
export const CityExplorer = ({ open, onClose, cityId, cityMeta, canAdd = true, addedIds = [], onAdd, onRemove }) => {
    const { lang, t } = useLang();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [detail, setDetail] = useState(null);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [retryTick, setRetryTick] = useState(0);

    useEffect(() => {
        if (!open || !cityId) return undefined;
        setData(null); setError(false); setDetail(null); setQ(""); setCat("");
        let alive = true;
        const ctrl = new AbortController();
        const watchdog = setTimeout(() => ctrl.abort(), 25000); // mai caricamento infinito
        api.get(`/public/cities/${cityId}/content`, { signal: ctrl.signal })
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setError(true); })
            .finally(() => clearTimeout(watchdog));
        return () => { alive = false; clearTimeout(watchdog); ctrl.abort(); };
    }, [open, cityId, retryTick]);

    const attractions = data?.attractions || [];
    const cats = useMemo(() => [...new Set(attractions.flatMap((a) => a.cats || []))], [attractions]);
    const filtered = useMemo(() => attractions.filter((a) => {
        const text = (pick(a.name, lang) + " " + pick(a.desc, lang)).toLowerCase();
        return (!q || text.includes(q.toLowerCase())) && (!cat || (a.cats || []).includes(cat));
    }), [attractions, q, cat, lang]);
    const mustSee = attractions.filter((a) => a.must_see);
    const added = (id) => addedIds.includes(id);

    if (!open) return null;
    const city = data?.city || {};
    const cityName = pick(cityMeta?.name, lang) || cityId;

    const AttrCard = ({ a, large = false }) => (
        <div className={`gold-card group overflow-hidden rounded-xl ${large ? "" : ""}`} data-testid={`xp-attr-${a.id}`}>
            <div className="relative h-44 overflow-hidden">
                <GoldImage src={photoUrl(cityId, a)} alt={pick(a.name, lang)} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/90 via-transparent to-transparent" />
                {a.must_see && (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-black">
                        <Star size={10} /> {t.builder.xp.mustSee}
                    </span>
                )}
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-black/60 px-2.5 py-1 text-[10px] text-amber-200">
                    <Clock size={10} /> ~{fmtMin(a.duration_min)}
                </span>
            </div>
            <div className="p-4">
                <h4 className="font-display text-base text-amber-50 leading-snug">{pick(a.name, lang)}</h4>
                <p className="mt-1.5 text-xs leading-relaxed text-stone-400 line-clamp-3">{pick(a.desc, lang)}</p>
                <div className="mt-3 flex items-center gap-2">
                    <button data-testid={`xp-story-${a.id}`} onClick={() => setDetail(a)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-amber-500/40 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-400/10">
                        <BookOpen size={12} /> {t.builder.xp.discoverStory}
                    </button>
                    {added(a.id) ? (
                        <button data-testid={`xp-remove-${a.id}`} onClick={() => onRemove?.(a.id)}
                            className="inline-flex items-center justify-center rounded-full border border-emerald-500/40 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-emerald-300 transition-colors hover:border-red-400/50 hover:text-red-300">
                            ✓
                        </button>
                    ) : (
                        <button data-testid={`xp-add-${a.id}`} onClick={() => canAdd && onAdd?.(a)} disabled={!canAdd}
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-amber-400 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-black transition-colors hover:bg-amber-300 disabled:opacity-40">
                            <Plus size={12} /> {t.builder.xp.add}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[70] overflow-y-auto bg-[#080909]/98 backdrop-blur-md" data-testid="city-explorer">
                <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
                    <div className="flex items-center justify-between">
                        <button data-testid="xp-back" onClick={detail ? () => setDetail(null) : onClose}
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-400 transition-colors hover:text-amber-300">
                            <ArrowLeft size={14} /> {detail ? pick(cityMeta?.name, lang) : t.builder.xp.back}
                        </button>
                        <button data-testid="xp-close" onClick={onClose} aria-label="close"
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-500/40 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                            <X size={17} />
                        </button>
                    </div>

                    {!data && !error && (
                        <div className="flex flex-col items-center py-32 text-center" data-testid="xp-loading">
                            <Loader2 size={34} className="animate-spin text-amber-400" />
                            <p className="mt-5 font-display text-xl text-amber-100">{t.builder.xp.loading.replace("{city}", cityName)}</p>
                            <p className="mt-2 text-xs text-stone-500">{t.builder.xp.loadingSub}</p>
                        </div>
                    )}
                    {error && (
                        <div className="py-32 text-center" data-testid="xp-error">
                            <p className="font-display text-xl text-amber-100">{cityName}</p>
                            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-stone-400">{t.builder.xp.errorMsg}</p>
                            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                                <button data-testid="xp-retry" onClick={() => { setError(false); setData(null); setRetryTick((x) => x + 1); }}
                                    className="rounded-full bg-amber-400 px-7 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-colors hover:bg-amber-300">
                                    {t.builder.xp.retry}
                                </button>
                                <button data-testid="xp-error-back" onClick={onClose}
                                    className="rounded-full border border-amber-500/50 px-7 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-200 transition-colors hover:bg-amber-400/10">
                                    {t.builder.xp.back}
                                </button>
                            </div>
                        </div>
                    )}

                    {data && !detail && (
                        <>
                            {/* HERO + INTRO */}
                            <div className="relative mt-6 overflow-hidden rounded-2xl border border-amber-500/25">
                                <GoldImage src={cityMeta?.image} alt={cityName} className="h-64 w-full object-cover sm:h-80" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/95 via-[#080909]/30 to-transparent" />
                                <div className="absolute bottom-0 p-7">
                                    <p className="text-[10px] uppercase tracking-[0.35em] text-amber-500">TOURVOYAGERS</p>
                                    <h2 className="mt-1 font-display text-4xl text-amber-50 sm:text-5xl" data-testid="xp-city-name">{cityName}</h2>
                                    {cityMeta?.tagline && <p className="mt-1 text-sm text-stone-300">{pick(cityMeta.tagline, lang)}</p>}
                                </div>
                            </div>
                            <p className="mt-6 max-w-3xl text-base leading-relaxed text-stone-300" data-testid="xp-intro">{pick(city.intro, lang)}</p>

                            <Section title={t.builder.xp.story} testId="xp-story">
                                <p className="max-w-3xl text-sm leading-relaxed text-stone-300">{pick(city.history, lang)}</p>
                            </Section>

                            {city.periods?.length > 0 && (
                                <Section title={t.builder.xp.periods} testId="xp-periods">
                                    <div className="grid gap-4 sm:grid-cols-3">
                                        {city.periods.map((p, i) => (
                                            <div key={i} className="gold-card rounded-xl p-5">
                                                <p className="font-mono-accent text-[10px] text-amber-500">{String(i + 1).padStart(2, "0")}</p>
                                                <h4 className="mt-1 font-display text-base text-amber-100">{pick(p.title, lang)}</h4>
                                                <p className="mt-2 text-xs leading-relaxed text-stone-400">{pick(p.text, lang)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            <div className="grid gap-6 lg:grid-cols-2">
                                <Section title={t.builder.xp.heritage} testId="xp-heritage">
                                    <p className="text-sm leading-relaxed text-stone-300">{pick(city.heritage, lang)}</p>
                                </Section>
                                <Section title={t.builder.xp.distinctive} testId="xp-distinctive">
                                    <p className="text-sm leading-relaxed text-stone-300">{pick(city.distinctive, lang)}</p>
                                </Section>
                            </div>

                            {city.tips?.length > 0 && (
                                <Section title={t.builder.xp.tips} testId="xp-tips">
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {city.tips.map((tip, i) => (
                                            <div key={i} className="flex items-start gap-3 rounded-xl border border-amber-500/20 bg-[#101011] p-4">
                                                <Sparkles size={14} className="mt-0.5 shrink-0 text-amber-400" />
                                                <p className="text-xs leading-relaxed text-stone-300">{pick(tip, lang)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            {/* GALLERIA ATTRAZIONI */}
                            <Section title={t.builder.xp.gallery} testId="xp-gallery">
                                {!canAdd && (
                                    <p className="mb-4 rounded-lg border border-amber-600/40 bg-amber-400/5 p-3 text-xs text-amber-200" data-testid="xp-need-city">
                                        {t.builder.xp.needCity.replace("{city}", cityName)}
                                    </p>
                                )}
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                    <div className="flex flex-1 items-center gap-2 rounded-lg border border-amber-500/20 bg-[#0b0b0c] px-3 py-2.5">
                                        <Search size={15} className="shrink-0 text-amber-500/70" />
                                        <input data-testid="xp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.builder.xp.searchPh}
                                            className="w-full bg-transparent text-sm text-stone-200 placeholder:text-stone-500 focus:outline-none" />
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        <button data-testid="xp-cat-all" onClick={() => setCat("")}
                                            className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${!cat ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/25 text-stone-400 hover:border-amber-400/60"}`}>
                                            {t.builder.xp.allCats}
                                        </button>
                                        {cats.map((c) => (
                                            <button key={c} data-testid={`xp-cat-${c}`} onClick={() => setCat(cat === c ? "" : c)}
                                                className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${cat === c ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/25 text-stone-400 hover:border-amber-400/60"}`}>
                                                {t.builder.interests[c] || c}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {mustSee.length > 0 && !q && !cat && (
                                    <div className="mt-6">
                                        <p className="text-[11px] uppercase tracking-[0.25em] text-amber-500">{t.builder.xp.mustSee}</p>
                                        <div className="mt-3 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                            {mustSee.map((a) => <AttrCard key={a.id} a={a} />)}
                                        </div>
                                        <div className="my-8 h-px bg-amber-500/15" />
                                    </div>
                                )}
                                {filtered.length === 0 ? (
                                    <p className="mt-6 text-sm text-stone-500" data-testid="xp-no-results">{t.builder.xp.noResults}</p>
                                ) : (
                                    <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="xp-attractions">
                                        {filtered.filter((a) => q || cat || !a.must_see).map((a) => <AttrCard key={a.id} a={a} />)}
                                    </div>
                                )}
                            </Section>
                            <div className="pb-12" />
                        </>
                    )}

                    {/* DETTAGLIO ATTRAZIONE — SCOPRI LA STORIA */}
                    {data && detail && (
                        <div className="mt-6" data-testid="xp-detail">
                            <div className="relative overflow-hidden rounded-2xl border border-amber-500/25">
                                <GoldImage src={photoUrl(cityId, detail)} alt={pick(detail.name, lang)} className="h-72 w-full object-cover sm:h-96" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#080909]/95 via-[#080909]/25 to-transparent" />
                                <div className="absolute bottom-0 flex w-full flex-wrap items-end justify-between gap-4 p-7">
                                    <div>
                                        <h2 className="font-display text-3xl text-amber-50 sm:text-4xl">{pick(detail.name, lang)}</h2>
                                        <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-amber-200/90"><Clock size={12} /> {t.builder.xp.duration}: ~{fmtMin(detail.duration_min)}</p>
                                    </div>
                                    {added(detail.id) ? (
                                        <button data-testid="xp-detail-remove" onClick={() => onRemove?.(detail.id)}
                                            className="rounded-full border border-emerald-500/50 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-300 transition-colors hover:border-red-400/60 hover:text-red-300">
                                            ✓ {t.builder.xp.added}
                                        </button>
                                    ) : (
                                        <button data-testid="xp-detail-add" onClick={() => canAdd && onAdd?.(detail)} disabled={!canAdd}
                                            className="rounded-full bg-amber-400 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 disabled:opacity-40">
                                            <Plus size={13} className="mr-1 inline" /> {t.builder.xp.add}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="mt-6 max-w-3xl text-base leading-relaxed text-stone-300">{pick(detail.desc, lang)}</p>
                            <Section title={t.builder.xp.discoverStory} testId="xp-detail-history">
                                <p className="max-w-3xl text-sm leading-relaxed text-stone-300">{pick(detail.history, lang)}</p>
                            </Section>
                            <div className="grid gap-6 lg:grid-cols-2">
                                <Section title={t.builder.xp.curiosity} testId="xp-detail-curiosity">
                                    <p className="text-sm leading-relaxed text-stone-300">{pick(detail.curiosity, lang)}</p>
                                </Section>
                                <Section title={t.builder.xp.practical} testId="xp-detail-practical">
                                    <p className="text-sm leading-relaxed text-stone-300">{pick(detail.practical, lang)}</p>
                                </Section>
                            </div>
                            {detail.lat && (
                                <p className="mt-8 inline-flex items-center gap-2 text-[11px] text-stone-500">
                                    <MapPin size={12} className="text-amber-500/70" /> {detail.lat.toFixed(5)}, {detail.lng.toFixed(5)}
                                </p>
                            )}
                            <div className="pb-12" />
                        </div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
};
